import React, { useEffect, useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { demeterChartsApi } from '../../../../Apis/Apis';
import { DemeterChartSeriesSourceModel, UpsertDemeterChartRequest } from '../../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../../Layouts/NavigationRoutes';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import useSearchParameters from '../../../Components/Navigation/Hooks/useSearchParametersHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from '../ChartBuilder.module.scss';
import ChartBuilderEditTitleModal from '../ChartBuilderChartDisplay/ChartBuilderEditTitleModal';
import { ChartBuilderRequest, defaultChartName } from '../ChartBuilderDefinitions';

interface IChartBuilderSaveActionProps {
    chartBuilderUpsertRequest: ChartBuilderRequest;
    setChartBuilderUpsertRequest: (chartBuilderRequest: ChartBuilderRequest) => void;
    isValidTransformationTypeAndValue: (dataSource: DemeterChartSeriesSourceModel) => boolean;
}

const ChartBuilderSaveAction: React.FC<IChartBuilderSaveActionProps> = (props: IChartBuilderSaveActionProps) => {
    const [translations] = useLanguage();
    const [saveAsModalOpen, setSaveAsModalOpen] = useState(false);
    const navigate = useNavigate();
    const [searchParameters] = useSearchParameters();

    const [isSaving, refreshUpsertDemeterChart, upsertDemeterChartResponse] = useApiWithoutAutoExecute(
        (request?: UpsertDemeterChartRequest) => {
            if (!props.chartBuilderUpsertRequest) {
                return null;
            }

            const filteredRequestRows = request?.rows
                .filter((x) => x.sources.some((y) => y.commodity))!
                .map((x) => ({ ...x, includeInChart: true, sources: x.sources.filter((y) => y.commodity) }))!;

            return demeterChartsApi.upsertDemeterChart({
                ...request!,
                rows: filteredRequestRows,
            });
        },
        {
            successMessage: translations.chartBuilder.messages.upsertChartSuccess,
            errorMessage: translations.chartBuilder.messages.upsertChartFailure,
        },
    );

    const isSavingDisabled = useMemo(() => {
        const validRowsOfChartbuilderRequest = props.chartBuilderUpsertRequest?.rows
            .filter((x) => x.sources.every((y) => props.isValidTransformationTypeAndValue(y)))
            .some((x) => x.sources.every((y) => y.commodity && y.region));

        return !validRowsOfChartbuilderRequest || isSaving;
    }, [props.chartBuilderUpsertRequest, isSaving]);

    useEffect(() => {
        if (!upsertDemeterChartResponse?.demeterChartGuid) {
            return;
        }

        navigate({
            pathname: `${NavigationRoutes.ChartBuilder}/${upsertDemeterChartResponse?.demeterChartGuid}/edit`,
            search: createSearchParams({ dataSeriesTab: searchParameters.dataSeriesTab }).toString(),
        });
    }, [upsertDemeterChartResponse]);

    const handleSave = (request: ChartBuilderRequest, isNewRequest?: boolean) => {
        if (isNewRequest && request.demeterChartGuid) {
            delete request.demeterChartGuid;
        }

        refreshUpsertDemeterChart(request as UpsertDemeterChartRequest);
    };

    return (
        <>
            <div className={styles.chart_display_buttons_row}>
                <LinkButton
                    title={translations.actions.save}
                    type={LinkButtonType.Blue}
                    onClick={() => handleSave(props.chartBuilderUpsertRequest)}
                    disabled={isSavingDisabled || props.chartBuilderUpsertRequest.name === defaultChartName}
                    track={{ eventCategory: 'ChartBuilder', eventAction: 'SaveClicked' }}
                />
                <LinkButton
                    title={translations.actions.saveAs}
                    type={LinkButtonType.White}
                    onClick={() => setSaveAsModalOpen(true)}
                    disabled={isSavingDisabled}
                    track={{ eventCategory: 'ChartBuilder', eventAction: 'SaveAsClicked' }}
                />
            </div>
            {saveAsModalOpen && (
                <ChartBuilderEditTitleModal
                    chartBuilderUpsertRequest={props.chartBuilderUpsertRequest}
                    setChartBuilderUpsertRequest={props.setChartBuilderUpsertRequest}
                    saveAsModalOpen={saveAsModalOpen}
                    setSaveAsModalOpen={setSaveAsModalOpen}
                    handleAction={() => handleSave(props.chartBuilderUpsertRequest, true)}
                />
            )}
        </>
    );
};

export default ChartBuilderSaveAction;
